/**
 * シーン動画クリップ尺 (sceneVideoSettings[key].duration) の既定値を決める純関数群。
 *
 * 生成直後 (useScenarioGeneration.handleScenarioGenerationSuccess) とリロード
 * (handleLoadScenario) の両経路が同じ優先順位で既定尺を決めるためにここへ集約する
 * (docs/requirements/per-scene-default-duration-fix-plan.md)。
 *
 * 優先順位:
 *   1. scenes.video_duration_seconds (AI 配分 + バックエンドでモデル対応尺にクランプ済み、
 *      または生成済み動画の実測尺)。ストーリーボードは復元条件つき (resolvePerSceneDefaultDuration)
 *   2. ストーリーボード: コマ尺合計 (想定尺は使わない)
 *   3. frame / reference: 想定尺 ÷ シーン数 (連続尺モデルのみ) → 無ければ 4 秒
 */

/** 想定尺ラベル ("15秒" / "5分" / "15") を秒数に変換する。
 *
 *  属性ツリーの option key (`do_72d8a1ac…` 等) は 16 進 ID に数字を含むため、
 *  先頭の数字列を拾う旧実装では「72秒」と誤解釈していた。ここでは「数字 + 秒 / 分 /
 *  素の数字」の形だけを受理し、それ以外は数字を含んでいても null を返す。
 *  呼び出し側は generation_metadata.duration (バックエンドが解決したラベル) を渡すこと。
 *  フォームの選択値 (option key) を渡してはならない。 */
export function parseIntendedDurationSeconds(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!s) return null;
  const sec = /^(\d+)\s*秒$/.exec(s);
  if (sec) {
    const n = Number(sec[1]);
    return n > 0 ? n : null;
  }
  const min = /^(\d+)\s*分$/.exec(s);
  if (min) {
    const n = Number(min[1]) * 60;
    return n > 0 ? n : null;
  }
  const raw = /^(\d+)$/.exec(s);
  if (raw) {
    const n = Number(raw[1]);
    return n > 0 ? n : null;
  }
  return null;
}

/** モデル対応尺が未取得のときに使う下限 (秒)。上限は設けない (SceneItem の範囲外スナップに委ねる)。 */
const EVEN_SPLIT_FALLBACK_MIN_SECONDS = 4;

/** Seedance / MiniMax H3 (連続尺モデル) のみ: 想定尺をシーン数で均等割りした既定尺。
 *
 *  Veo / Kling / Gemini Omni は per-scene 候補が離散的で単純分割では候補から外れるため
 *  null を返す (呼び出し側が 4 秒にフォールバック)。
 *
 *  クランプは supportedDurations (generation_models.supported_durations。-1 = 「自動」は
 *  除外) の min / max で行う。未取得 (undefined / null / 空) のときは下限 4 のみを適用し
 *  上限は掛けない: 小さい値で初期化してしまうと、後からモデルが解決しても SceneItem の
 *  範囲外スナップは「範囲内の値」を補正しないため二度と広がらない。大きい値なら範囲外
 *  としてスナップされ下方修正されるので、補正は上方向にしか起きない。 */
export function computeEvenSplitDefaultDuration(
  videoModel: string,
  intendedTotalSeconds: number | null,
  sceneCount: number,
  supportedDurations?: number[] | null,
): number | null {
  if (!videoModel.startsWith('seedance') && !videoModel.startsWith('minimax')) return null;
  if (!intendedTotalSeconds || sceneCount <= 0) return null;
  const per = Math.round(intendedTotalSeconds / sceneCount);
  const positives = (supportedDurations ?? []).filter((d) => typeof d === 'number' && d > 0);
  if (positives.length === 0) {
    return Math.max(EVEN_SPLIT_FALLBACK_MIN_SECONDS, per);
  }
  const min = Math.min(...positives);
  const max = Math.max(...positives);
  return Math.min(max, Math.max(min, per));
}

/** ストーリーボードモードのコマ尺 (storyboard_duration_seconds) 既定値。
 *  useVideoHandlers の STORYBOARD_DEFAULT_CUT_DURATION と一致させること。 */
export const STORYBOARD_DEFAULT_CUT_DURATION = 2;

/** ストーリーボードモードの動画クリップ尺の既定値: 本編シーンのコマ尺合計。
 *  出力は動画 1 本なのでタイムライン総尺をそのまま既定にする (想定尺は使わない)。
 *  ブランドカット (タイトルカード/エンドカード = brand_cut_position あり) は本編とは別に
 *  合成される完成済みカードなので、生成する本編の尺デフォルトには含めない。
 *  モデル上限へのクランプ・離散候補へのスナップは SceneItem 側の storyboardDurationOptions で行う。 */
export function computeStoryboardTotalDuration(
  scenes: Array<{ storyboard_duration_seconds?: number | null; brand_cut_position?: 'start' | 'end' | null }>,
): number {
  return scenes
    .filter((scene) => !scene.brand_cut_position)
    .reduce(
      (acc, scene) => acc + (scene.storyboard_duration_seconds ?? STORYBOARD_DEFAULT_CUT_DURATION),
      0,
    );
}

export interface ResolvePerSceneDefaultDurationInput {
  /** 制作方式がストーリーボード (絵コンテ丸ごと) か */
  isStoryboardMode: boolean;
  /** scenes.video_duration_seconds (AI 配分 / 生成済み動画の実測尺)。未保存なら null / undefined */
  storedDuration: number | null | undefined;
  /** 動画が紐づいているか (!!scene.video_material_id) */
  hasGeneratedVideo: boolean;
  /** computeStoryboardTotalDuration の結果 (非ストーリーボードでは参照されない) */
  storyboardTotalDuration: number;
  /** computeEvenSplitDefaultDuration の結果 (非連続尺モデルでは null) */
  evenSplitDefault: number | null;
}

/**
 * 保存尺 (scenes.video_duration_seconds) を「次に生成するときの尺」に直す: 整数秒に丸める (最小 1)。
 *
 * 生成済み動画の実測尺は 4.04 秒のような小数になるが、生成リクエストの尺と消費ポイントのバッジは
 * 整数秒で扱う (モデルの対応尺も整数)。**DB の実測尺そのものは書き換えない** (タイムライン・
 * 書き出し・BGM 尺計算が正として読む値なので、ここで丸めるのは UI の生成尺の初期値だけ)。
 */
export function toGenerationDurationSeconds(seconds: number): number {
  return Math.max(1, Math.round(seconds));
}

/** 1 シーンの既定尺を決める (生成直後 / ロードの両経路で共用)。
 *
 *  ストーリーボードの保存尺は「生成済み動画あり (= 実測尺の記録)」または
 *  「保存尺 ≥ コマ尺合計」のときだけ保持し、それ以外はコマ尺合計へ戻す
 *  (コマ尺の編集で合計が伸びたのに古い保存尺が残る事故を避ける。従来のロード経路と同一条件)。
 *  frame / reference の保存尺は無条件に優先する。保存尺は整数秒に丸めて返す (toGenerationDurationSeconds)。 */
export function resolvePerSceneDefaultDuration(input: ResolvePerSceneDefaultDurationInput): number {
  const { isStoryboardMode, storedDuration, hasGeneratedVideo, storyboardTotalDuration, evenSplitDefault } = input;
  const stored = typeof storedDuration === 'number' && storedDuration > 0 ? storedDuration : null;
  const useStored = stored != null
    && (!isStoryboardMode || hasGeneratedVideo || stored >= storyboardTotalDuration);
  if (useStored) return toGenerationDurationSeconds(stored);
  if (isStoryboardMode) return storyboardTotalDuration;
  return evenSplitDefault ?? 4;
}
